/* eslint-disable react-hooks/rules-of-hooks */
import React, { createContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "../app";
import { isCloudSite, isElectronApp } from "../electron-modules";
import { getLocalizedURL } from "../localization";
import { engineCloudURL, openExternalLink, redirectToLogin } from "../urls";
import SuperTokens from "supertokens-web-js";
import { AccessType } from "../types/project";
import Session from "supertokens-web-js/recipe/session";
import EmailPassword from "supertokens-web-js/recipe/emailpassword";
import {signOut as stSignOut} from "supertokens-web-js/lib/build/recipe/emailpassword";
import { useWebSockets } from "../hooks";

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
}

interface ProjectPermissions {
  type: AccessType;
  access: boolean;
  create: boolean;
  delete: boolean;
  edit: boolean;
}

const appCodeRefreshRate = 2000;
const appCodeMaxAttempts = 100;
const originalFetch = fetch;

export const asyncTimeout = (ms) => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

class AuthHandler {
  public locked = false;
  private accessTokenKey = "st-access-token"
  private refreshTokenKey = "st-refresh-token"

  get accessToken() {
    return localStorage.getItem(this.accessTokenKey);
  }

  get refreshToken() {
    return localStorage.getItem(this.refreshTokenKey);
  }

  clearTokens() {
    localStorage.removeItem(this.accessTokenKey);
    localStorage.removeItem(this.refreshTokenKey);
  }

  checkForKey(headers, tokenKey: string) {
    if (!headers.has(tokenKey)) return;
    const token = headers.get(tokenKey);
    localStorage.setItem(tokenKey, token);
  }

  updateTokens(headers) {
    this.checkForKey(headers, this.accessTokenKey);
    this.checkForKey(headers, this.refreshTokenKey);
  }

  async wait() {
    while (this.locked) {
      await asyncTimeout(100);
    }
  }

  async fetch(input: string, init: RequestInit) {
    const {headers = {}, ...others} = init;

    const response = await originalFetch(input, {
      headers: {
        'Authorization': `Bearer ${this.accessToken}`,
        ...headers,
      },
      ...others
    });

    return response;
  }

  async update() {
    this.locked = true;

    const url = `${engineCloudURL}/auth/session/refresh`;

    const response = await originalFetch(url, {
      method: "POST",
      headers: {
        'rid': 'session',
        'Authorization': `Bearer ${this.refreshToken}`
      }
    });

    let success = response.status === 200;

    if (success) {
      this.updateTokens(response.headers);
    }

    this.locked = false;

    return success;
  }
}

const handler = new AuthHandler();

const initCustomFetch = () => {
  const customFetch = async (input: string, init: RequestInit = {}) => {
    if (!input.includes(engineCloudURL)) {
      return originalFetch(input, init);
    }

    if (handler.locked) {
      await handler.wait();
    }

    const response = await handler.fetch(input, init);

    if (response.status !== 401) {
      return response;
    }

    if (handler.locked) {
      return customFetch(input, init);
    }

    const updated = await handler.update();

    if (updated) {
      return customFetch(input, init);
    } else {
      throw new Error("Unauthorized");
    }
  };

  window['fetch'] = customFetch;
};

const initSuperTokens = () => {
  SuperTokens.init({
    appInfo: {
      appName: "Solv3D Engine Cloud",
      apiDomain: window.location.origin,
      apiBasePath: "/auth"
    },
    recipeList: [
      Session.init(),
      EmailPassword.init(),
    ]
  });
};

const initialUserState = {
  id: null,
  email: null,
  firstName: null,
  lastName: null
} as User;

const adminPermissions = {
  type: AccessType.Admin,
  access: true,
  create: true,
  delete: true,
  edit: true
} as ProjectPermissions;

const emptyPermissions = {
  type: AccessType.ViewOnly,
  access: false,
  create: false,
  delete: false,
  edit: false
} as ProjectPermissions;

export const AuthContext = createContext({
  user: null as User,
  permissions: null as ProjectPermissions,
  loggedIn: false,
  initialized: false,
  linkAccount: async () => false,
  signOut: () => {},
  getAppCode: async (activate: boolean) => null as string,
});

export const AuthProvider = (props) => {
  const { i18n, t } = useTranslation();

  const websockets = useWebSockets();

  const [state, setState] = useState({
    user: {...initialUserState},
    permissions: null,
    initialized: false
  });

  const linkAccount = async () => {
    try {
      const appCode = await getAppCode();
      const url = getLocalizedURL(
        `${engineCloudURL}/link?code=${appCode}`,
        i18n.language
      );

      openExternalLink(url);
      return useAppCode(appCode);
    } catch {
      toast.error(t('user-account.error_linking_account'));
      return false;
    }
  };

  const signOut = async () => {
    if (isCloudSite) {
      await stSignOut();
      redirectToLogin();
    } else {
      setState({
        ...state,
        user: {...initialUserState}
      });

      handler.clearTokens();
      toast.success(t('user-account.account_unlinked'));
    }

    websockets.disconnect();
  };

  const getAppCode = async (activate=false) => {
    const url = `${engineCloudURL}/api/app_codes/request`;
    const response = await fetch(url);
    const data = await response.json();
    const appCode = String(data.access_code);

    if (activate) {
      const url = `${engineCloudURL}/api/app_codes/activate/${appCode}`;
      await fetch(url, {method: "PUT"});
    }

    return appCode;
  };

  /** Consume app code until it returns a success code */
  const useAppCode = async (appCode: string): Promise<boolean> => {
    if (!appCode) return true;

    return new Promise((resolve, reject) => {
      let retryAttempt = 0;

      const interval = setInterval(async () => {
        if (retryAttempt >= appCodeMaxAttempts) {
          resolve(false);
          clearInterval(interval);
          toast.error(t('user-account.error_linking_account'));
          return;
        };

        const url = `${engineCloudURL}/api/app_codes/consume/${appCode}`;
        const response = await fetch(url);
        const success = response.status === 200;

        if (success) {
          handler.updateTokens(response.headers);
          await initialize();
          toast.success(t('user-account.account_linked'));
          clearInterval(interval);
          resolve(true);
        }

        retryAttempt += 1;
      }, appCodeRefreshRate);
    });
  };

  const getUser = async () : Promise<User> => {
    try {
      const url = `${engineCloudURL}/api/users/me`;
      const response = await fetch(url);
      if (response.status !== 200) {
        return initialUserState;
      }

      const user = await response.json();
      return user;
    } catch {
      return initialUserState;
    }
  };

  const getPermissions = async () : Promise<ProjectPermissions> => {
    try {
      const url = `${window.location.pathname}/permissions`;
      const response = await fetch(url);
      if (response.status !== 200) {
        return emptyPermissions;
      }

      const permissions = await response.json();
      return permissions;
    } catch {
      return emptyPermissions;
    }
  };

  const initialize = async () => {
    const newState = {...state};

    if (isCloudSite || isElectronApp) {
      const user = await getUser();
      newState.user = {...user};

      await websockets.connect(handler.accessToken);

      if (!user.id) {
        handler.clearTokens();
      }
    }

    if (isCloudSite) {
      const permissions = await getPermissions();
      newState.permissions = {...permissions};
    } else if (isElectronApp) {
      newState.permissions = {...adminPermissions};
    } else {
      newState.permissions = {
        ...emptyPermissions,
        access: true
      };
    }

    setState({...newState, initialized: true});
  };

  /** Initialize on first load */
  useEffect(() => {
    initialize();
  }, []);

  const {user, permissions, initialized} = state;
  const loggedIn = !!user.id;

  return (
    <AuthContext.Provider value={{
      user,
      permissions,
      initialized,
      loggedIn,
      linkAccount,
      signOut,
      getAppCode,
    }}>
      {props.children}
    </AuthContext.Provider>
  );
};


if (isCloudSite) {
  // Cloud site uses cookie based auth. Fetch function
  // is automatically patched with supertokens-web-js
  initSuperTokens();
} else if (isElectronApp) {
  // Initialize custom header based auth with localStorage.
  // Fetch function is patched with a custom implementation
  initCustomFetch();
}